import type { ScoreZone, SecurityLevel, SecurityConfig } from '@/types/auth';

/** Security presets */
export const SECURITY_PRESETS: Record<SecurityLevel, SecurityConfig> = {
  convenient: {
    level: 'convenient',
    acceptThreshold: 60,
    rejectThreshold: 35,
    continuousAuthInterval: 120000,
    stepUpRequired: false,
  },
  balanced: {
    level: 'balanced',
    acceptThreshold: 70,
    rejectThreshold: 50,
    continuousAuthInterval: 60000,
    stepUpRequired: true,
  },
  strict: {
    level: 'strict',
    acceptThreshold: 80,
    rejectThreshold: 60,
    continuousAuthInterval: 30000,
    stepUpRequired: true,
  },
};

/** Determine the score zone for a given score and security level */
export function getScoreZone(
  score: number,
  level: SecurityLevel = 'balanced',
): ScoreZone {
  const config = SECURITY_PRESETS[level];
  if (score >= config.acceptThreshold) return 'accept';
  if (score >= config.rejectThreshold) return 'gray_zone';
  return 'reject';
}

/** Get the color for a score zone */
export function getZoneColor(zone: ScoreZone): string {
  switch (zone) {
    case 'accept':
      return '#00d26a';
    case 'gray_zone':
      return '#f5a623';
    case 'reject':
      return '#e74c3c';
  }
}

/** Get the Tailwind color class for a score zone */
export function getZoneTailwind(zone: ScoreZone): string {
  switch (zone) {
    case 'accept':
      return 'text-accent-success';
    case 'gray_zone':
      return 'text-accent-warning';
    case 'reject':
      return 'text-accent-danger';
  }
}

/** Get zone background class */
export function getZoneBgTailwind(zone: ScoreZone): string {
  switch (zone) {
    case 'accept':
      return 'bg-accent-success';
    case 'gray_zone':
      return 'bg-accent-warning';
    case 'reject':
      return 'bg-accent-danger';
  }
}

/** Get the label for a score zone */
export function getZoneLabel(zone: ScoreZone): string {
  switch (zone) {
    case 'accept':
      return 'Aceito';
    case 'gray_zone':
      return 'Zona Cinza';
    case 'reject':
      return 'Rejeitado';
  }
}

/** Format a score as percentage */
export function formatScore(score: number): string {
  return `${Math.round(score)}%`;
}

/** Get descriptive text for a score */
export function getScoreDescription(score: number): string {
  if (score >= 90) return 'Excelente correspondência biométrica';
  if (score >= 80) return 'Forte correspondência biométrica';
  if (score >= 70) return 'Boa correspondência biométrica';
  if (score >= 60) return 'Correspondência moderada';
  if (score >= 50) return 'Correspondência fraca — verificação adicional recomendada';
  if (score >= 35) return 'Correspondência insuficiente';
  return 'Assinatura não reconhecida';
}

/** Get WPM quality label */
export function getWpmLabel(wpm: number): string {
  if (wpm >= 80) return 'Muito rápido';
  if (wpm >= 60) return 'Rápido';
  if (wpm >= 40) return 'Normal';
  if (wpm >= 25) return 'Lento';
  return 'Muito lento';
}

/** Get accuracy quality label */
export function getAccuracyLabel(accuracy: number): string {
  if (accuracy >= 0.98) return 'Impecável';
  if (accuracy >= 0.95) return 'Excelente';
  if (accuracy >= 0.90) return 'Boa';
  if (accuracy >= 0.80) return 'Regular';
  return 'Baixa';
}

/** Get consistency quality label */
export function getConsistencyLabel(consistency: number): string {
  if (consistency >= 0.85) return 'Muito consistente';
  if (consistency >= 0.70) return 'Consistente';
  if (consistency >= 0.50) return 'Moderada';
  return 'Variável';
}

/** Interpolate between two colors based on t (0-1) */
export function interpolateColor(
  color1: [number, number, number],
  color2: [number, number, number],
  t: number,
): string {
  const r = Math.round(color1[0] + (color2[0] - color1[0]) * t);
  const g = Math.round(color1[1] + (color2[1] - color1[1]) * t);
  const b = Math.round(color1[2] + (color2[2] - color1[2]) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

/** Get color for a score on the continuous spectrum */
export function getScoreColor(score: number): string {
  if (score >= 70) {
    // Green spectrum
    const t = Math.min((score - 70) / 30, 1);
    return interpolateColor([245, 166, 35], [0, 210, 106], t);
  }
  if (score >= 50) {
    // Yellow to orange
    const t = (score - 50) / 20;
    return interpolateColor([231, 76, 60], [245, 166, 35], t);
  }
  // Red spectrum
  return '#e74c3c';
}

/** Compute session quality based on metrics */
export function computeSessionQuality(
  wpm: number,
  accuracy: number,
  consistency: number,
): { score: number; label: string } {
  // Weighted quality score
  const score = accuracy * 0.4 + consistency * 0.35 + Math.min(wpm / 100, 1) * 0.25;
  const percent = Math.round(score * 100);

  let label: string;
  if (percent >= 90) label = 'Excelente';
  else if (percent >= 75) label = 'Bom';
  else if (percent >= 60) label = 'Aceitável';
  else if (percent >= 40) label = 'Regular';
  else label = 'Insuficiente';

  return { score: percent, label };
}
